// ============================================================
// Income List Page (/income)
// ============================================================

import { useState } from 'react';
import { Box, Typography, Button, Stack, IconButton, TextField, InputAdornment, Tooltip, Chip, Card, CardContent, Grid, MenuItem, Select, FormControl, InputLabel, Link } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, IndianRupee, Layers, Calendar, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { IncomeModal } from './components/IncomeModal';
import { useIncomes, useIncomeCategories, useIncomeSummaryTotal, useCreateIncome, useUpdateIncome, useDeleteIncome } from '@/hooks/useIncomes';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';

export function IncomeListPage() {
  usePageMeta('Incomes List | Income Management', [
    { label: 'Dashboard', path: '/' },
    { label: 'Incomes' },
  ]);

  // Filters State
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [deleteIncome, setDeleteIncome] = useState(null);

  // Queries
  const { data: categories = [] } = useIncomeCategories(true);
  const {
    data: incomes = [],
    isLoading,
    error,
    refetch,
  } = useIncomes({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    category_id: categoryId !== 'ALL' ? categoryId : undefined,
  });

  const { data: summaryTotal } = useIncomeSummaryTotal({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  });

  // Mutations
  const createMutation = useCreateIncome();
  const updateMutation = useUpdateIncome();
  const deleteMutation = useDeleteIncome();

  // Local filtering by search text
  const filteredIncomes = (incomes || []).filter((inc) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const descMatch = (inc.description || '').toLowerCase().includes(q);
    const catName = (inc.category?.name || '').toLowerCase().includes(q);
    const amountMatch = String(inc.amount || '').includes(q);
    return descMatch || catName || amountMatch;
  });

  // Calculations
  const calculatedTotal = (filteredIncomes || []).reduce(
    (acc, curr) => acc + Number(curr.amount || 0),
    0
  );

  const displayTotal =
    summaryTotal?.total_income !== undefined && !startDate && !endDate && categoryId === 'ALL'
      ? Number(summaryTotal.total_income)
      : calculatedTotal;

  const handleOpenAdd = () => {
    setEditingIncome(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (inc) => {
    setEditingIncome(inc);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload) => {
    try {
      if (editingIncome) {
        await updateMutation.mutateAsync({ id: editingIncome.id, payload });
        toast.success('Income record updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Income record created successfully');
      }
      setModalOpen(false);
      setEditingIncome(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save income');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteIncome) return;
    try {
      await deleteMutation.mutateAsync(deleteIncome.id);
      toast.success('Income record deleted successfully');
      setDeleteIncome(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete income');
    }
  };

  const columns = [
    {
      field: 'income_date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => {
        const catName = params.row.category?.name || 'Uncategorized';
        return (
          <Chip
            label={catName}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'amount',
      headerName: 'Amount (₹)',
      flex: 1.2,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="text.primary">
          ₹{Number(params.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || 'No description provided.'}
        </Typography>
      ),
    },
    {
      field: 'receipt_url',
      headerName: 'Receipt',
      flex: 1,
      minWidth: 110,
      renderCell: (params) =>
        params.value && params.value !== 'string' ? (
          <Link
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 600 }}
          >
            Receipt <ExternalLink size={14} />
          </Link>
        ) : (
          <Typography variant="caption" color="text.muted">
            N/A
          </Typography>
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Edit Income">
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)} color="primary">
              <Edit size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Income">
            <IconButton size="small" onClick={() => setDeleteIncome(params.row)} color="error">
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <PageHeader
        title="Income Management"
        subtitle="Track, filter, and record school income sources."
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenAdd}
            sx={{ fontWeight: 600, px: 3, textTransform: 'none' }}
          >
            Add Income
          </Button>
        }
      />

      {/* Metric Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card elevation={0} sx={{ border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radius.md }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: tokens.radius.sm,
                  backgroundColor: tokens.colors.primary[50],
                  color: tokens.colors.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IndianRupee size={24} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Income
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  ₹{displayTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card elevation={0} sx={{ border: `1px solid ${tokens.colors.border}`, borderRadius: tokens.radius.md }}>
            <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: tokens.radius.sm,
                  backgroundColor: tokens.colors.success[50],
                  color: tokens.colors.success.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Layers size={24} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total Records Listed
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {filteredIncomes.length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box sx={{ width: '100%', p: 2.5, bgcolor: 'background.paper', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          <TextField
            size="small"
            placeholder="Search description, category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: { xs: '1 1 100%', sm: '2 1 240px' } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryId}
              label="Category"
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <MenuItem value="ALL">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => {
              const val = e.target.value;
              setStartDate(val);
              if (endDate && val && val > endDate) {
                toast.error('Start Date cannot be later than End Date');
              }
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { max: endDate || undefined },
            }}
            error={Boolean(startDate && endDate && startDate > endDate)}
            helperText={startDate && endDate && startDate > endDate ? 'Must be ≤ End Date' : undefined}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' } }}
          />

          <TextField
            size="small"
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => {
              const val = e.target.value;
              setEndDate(val);
              if (startDate && val && val < startDate) {
                toast.error('End Date cannot be earlier than Start Date');
              }
            }}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: startDate || undefined },
            }}
            error={Boolean(startDate && endDate && startDate > endDate)}
            helperText={startDate && endDate && startDate > endDate ? 'Must be ≥ Start Date' : undefined}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 160px' } }}
          />

          {(startDate || endDate || categoryId !== 'ALL' || search) && (
            <Button
              size="small"
              onClick={() => {
                setSearch('');
                setCategoryId('ALL');
                setStartDate('');
                setEndDate('');
              }}
              color="inherit"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Main Table */}
      {error ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Loader message="Loading incomes..." />
      ) : (
        <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, overflow: 'hidden' }}>
          <DataGrid
            autoHeight
            rows={filteredIncomes}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      )}

      {/* Create / Edit Modal */}
      <IncomeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmitModal}
        loading={createMutation.isPending || updateMutation.isPending}
        income={editingIncome}
        categories={categories}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={!!deleteIncome}
        onClose={() => setDeleteIncome(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Income Record"
        message="Are you sure you want to delete this income record? This action cannot be undone."
        confirmText="Delete Record"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
